import { useState, useCallback, useRef } from "react";

import { UploadProgressData } from "../components/UploadProgress";

export interface UploadState {
  isUploading: boolean;
  progress?: UploadProgressData;
  fileName?: string;
  fileSize?: number;
  error?: string;
  isComplete?: boolean;
  abortController?: AbortController;
}

export interface UploadProgressHook {
  uploadState: UploadState;
  startUpload: (fileName: string, fileSize: number) => void;
  updateProgress: (progress: UploadProgressData) => void;
  setError: (error: string) => void;
  completeUpload: () => void;
  resetUpload: () => void;
  cancelUpload: () => void;
}

export const useUploadProgress = (): UploadProgressHook => {
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const startUpload = useCallback((fileName: string, fileSize: number) => {
    // Create a new abort controller for this upload
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setUploadState({
      isUploading: true,
      fileName,
      fileSize,
      abortController,
      progress: {
        totalParts: 1,
        completedParts: 0,
        totalBytes: fileSize,
        uploadedBytes: 0,
      },
    });
  }, []);

  const updateProgress = useCallback((progress: UploadProgressData) => {
    setUploadState((prev) => ({
      ...prev,
      progress,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setUploadState((prev) => ({
      ...prev,
      isUploading: false,
      error,
    }));
  }, []);

  const completeUpload = useCallback(() => {
    setUploadState((prev) => ({
      ...prev,
      isUploading: false,
      isComplete: true,
      progress: prev.progress
        ? {
            ...prev.progress,
            completedParts: prev.progress.totalParts,
            uploadedBytes: prev.progress.totalBytes,
          }
        : undefined,
    }));
  }, []);

  const resetUpload = useCallback(() => {
    setUploadState({
      isUploading: false,
      isComplete: false,
    });
    abortControllerRef.current = null;
  }, []);

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setUploadState({
      isUploading: false,
      isComplete: false,
    });
    abortControllerRef.current = null;
  }, []);

  return {
    uploadState,
    startUpload,
    updateProgress,
    setError,
    completeUpload,
    resetUpload,
    cancelUpload,
  };
};
